import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
} from "react";

import { trackEvent } from "~/lib/analytics";

import type {
    TourConfig,
    TourContextValue,
    TourState,
} from "../types/tour-config";
import { markTourCompleted, markTourSkipped } from "../utils/tour-storage";

// AIDEV-NOTE: Global tour state management context
// Provides tour state and actions to all components in the tree

const TourContext = createContext<TourContextValue | null>(null);

interface TourProviderProps {
    children: React.ReactNode;
    /** Available tours - passed in from the app level */
    tours: TourConfig[];
}

export function TourProvider({ children, tours }: TourProviderProps) {
    const [state, setState] = useState<TourState>({
        isActive: false,
        currentTourId: null,
        currentStepIndex: 0,
        welcomeModalTourId: null,
    });

    // Get current tour configuration
    const currentTour = useMemo(() => {
        if (!state.currentTourId) return null;
        return tours.find((t) => t.id === state.currentTourId) || null;
    }, [state.currentTourId, tours]);

    // Helper to get any tour by ID (for welcome modal before tour starts)
    const getTourById = useCallback(
        (tourId: string) => {
            return tours.find((t) => t.id === tourId) || null;
        },
        [tours],
    );

    // Get current step
    const currentStep = useMemo(() => {
        if (!currentTour) return null;
        return currentTour.steps[state.currentStepIndex] || null;
    }, [currentTour, state.currentStepIndex]);

    // Calculate derived state
    const totalSteps = currentTour?.steps.length || 0;
    const hasNext = state.currentStepIndex < totalSteps - 1;
    const hasPrevious = state.currentStepIndex > 0;

    // AIDEV-NOTE: openWelcomeModal is tour-aware — only the matching WelcomeModal will open.
    // Previously a bare boolean that caused ALL WelcomeModals to open when any tour triggered.
    const openWelcomeModal = useCallback((tourId: string) => {
        setState((prev) => ({ ...prev, welcomeModalTourId: tourId }));
    }, []);

    // Action: Close welcome modal
    const closeWelcomeModal = useCallback(() => {
        setState((prev) => ({ ...prev, welcomeModalTourId: null }));
    }, []);

    // Check if a specific tour's welcome modal is open
    const isWelcomeModalOpenForTour = useCallback(
        (tourId: string) => state.welcomeModalTourId === tourId,
        [state.welcomeModalTourId],
    );

    // Action: Start a tour
    const startTour = useCallback(
        (tourId: string) => {
            const tour = tours.find((t) => t.id === tourId);
            if (!tour) {
                console.error(`[TourContext] Tour not found: ${tourId}`);
                return;
            }

            // Track analytics
            trackEvent("tour started", {
                tourId,
                page: tour.page,
            });

            // IMPORTANT: Close welcome modal first (state update)
            setState({
                isActive: true,
                currentTourId: tourId,
                currentStepIndex: 0,
                welcomeModalTourId: null, // This closes the welcome modal
            });

            // Call onEnter for first step
            tour.steps[0]?.onEnter?.();
        },
        [tours],
    );

    // Action: End the tour
    const endTour = useCallback(() => {
        if (!currentTour || !state.isActive) return;

        // Call onExit for current step
        currentStep?.onExit?.();

        // Mark tour as completed
        markTourCompleted(currentTour.id);

        // Track analytics
        trackEvent("tour completed", {
            tourId: currentTour.id,
            page: currentTour.page,
            stepsCompleted: state.currentStepIndex + 1,
            totalSteps: currentTour.steps.length,
        });

        // Reset state
        setState({
            isActive: false,
            currentTourId: null,
            currentStepIndex: 0,
            welcomeModalTourId: null,
        });
    }, [currentTour, currentStep, state.isActive, state.currentStepIndex]);

    // Action: Skip the tour
    const skipTour = useCallback(
        (tourIdToSkip?: string) => {
            // Allow skipping by ID (for welcome modal) or use current tour
            const tourId = tourIdToSkip || currentTour?.id;
            if (!tourId) return;

            const tour = tours.find((t) => t.id === tourId);
            if (!tour) return;

            // Mark tour as skipped
            markTourSkipped(tourId);

            // Track analytics
            trackEvent("tour skipped", {
                tourId,
                page: tour.page,
                stepsCompleted: state.currentStepIndex,
                totalSteps: tour.steps.length,
            });

            // Reset state
            setState({
                isActive: false,
                currentTourId: null,
                currentStepIndex: 0,
                welcomeModalTourId: null,
            });
        },
        [currentTour, state.currentStepIndex, tours],
    );

    // Action: Go to next step
    const nextStep = useCallback(() => {
        if (!hasNext || !currentTour) return;

        // Call onExit for current step
        currentStep?.onExit?.();

        const nextIndex = state.currentStepIndex + 1;

        // Track step view
        trackEvent("tour step viewed", {
            tourId: currentTour.id,
            stepId: currentTour.steps[nextIndex]?.id,
            stepIndex: nextIndex,
            totalSteps: currentTour.steps.length,
        });

        setState((prev) => ({
            ...prev,
            currentStepIndex: nextIndex,
        }));

        // Call onEnter for next step
        currentTour.steps[nextIndex]?.onEnter?.();
    }, [hasNext, currentTour, currentStep, state.currentStepIndex]);

    // Action: Go to previous step
    const previousStep = useCallback(() => {
        if (!hasPrevious || !currentTour) return;

        // Call onExit for current step
        currentStep?.onExit?.();

        const prevIndex = state.currentStepIndex - 1;

        setState((prev) => ({
            ...prev,
            currentStepIndex: prevIndex,
        }));

        // Call onEnter for previous step
        currentTour.steps[prevIndex]?.onEnter?.();
    }, [hasPrevious, currentTour, currentStep, state.currentStepIndex]);

    // AIDEV-NOTE: Memoize context value to prevent unnecessary re-renders of consumers
    const value: TourContextValue = useMemo(
        () => ({
            ...state,
            currentTour,
            currentStep,
            totalSteps,
            hasNext,
            hasPrevious,
            startTour,
            endTour,
            nextStep,
            previousStep,
            skipTour,
            openWelcomeModal,
            closeWelcomeModal,
            isWelcomeModalOpenForTour,
            getTourById,
        }),
        [
            state,
            currentTour,
            currentStep,
            totalSteps,
            hasNext,
            hasPrevious,
            startTour,
            endTour,
            nextStep,
            previousStep,
            skipTour,
            openWelcomeModal,
            closeWelcomeModal,
            isWelcomeModalOpenForTour,
            getTourById,
        ],
    );

    return (
        <TourContext.Provider value={value}>{children}</TourContext.Provider>
    );
}

/** Hook to access tour context */
export function useTourContext(): TourContextValue {
    const context = useContext(TourContext);
    if (!context) {
        throw new Error("useTourContext must be used within TourProvider");
    }
    return context;
}
